import { useEffect, useRef, useState, type DragEvent } from "react";
import { UploadCloud } from "lucide-react";
import { DataTable, type RecordData } from "./Workflow";
import { ErrorState, LoadingState } from "./Ui";
import { formatDate } from "../api";
import { periodText } from "./PortalPerformance";
import "./PortalReportUpload.css";

type Send = (path: string, body?: RecordData | FormData, method?: string) => Promise<any>;
type Result = { file_name: string; jobs: { new: number; updated: number; period: { start: string; end: string } | null } | null; sheets_updated: number; sheet_problems: number; google_ads: { status: string; sent: number } | null };

const plural = (n: number, one: string) => `${n.toLocaleString("en-US")} ${one}${n === 1 ? "" : "s"}`;

/**
 * The owner's appointment report, dropped straight into the portal (Shawn,
 * 2026-10-21): it used to be emailed every morning for the agency to upload.
 * NOLA turns it into booked conversions for Google Ads.
 */
export default function PortalReportUpload({ clientId, send }: { clientId: number; send: Send }) {
  const [uploads, setUploads] = useState<RecordData[] | null>(null), [listError, setListError] = useState("");
  const [file, setFile] = useState<File | null>(null), [busy, setBusy] = useState(false), [over, setOver] = useState(false);
  const [result, setResult] = useState<Result | null>(null), [error, setError] = useState(""), [tick, setTick] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const path = `/api/portal/appointment-reports?client_id=${clientId}`;
  useEffect(() => {
    let cancelled = false;
    setListError("");
    send(path).then((r) => { if (!cancelled) setUploads(r.uploads || []); }).catch((e) => { if (!cancelled) setListError(e.message); });
    return () => { cancelled = true; };
  }, [clientId, tick]);

  function choose(next: File | null | undefined) {
    if (!next) return;
    setResult(null);
    setError(/\.(xlsx|xls|csv)$/i.test(next.name) ? "" : "Choose the .xlsx or .csv report as it came out of your system.");
    setFile(/\.(xlsx|xls|csv)$/i.test(next.name) ? next : null);
  }
  function drop(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setOver(false);
    choose(e.dataTransfer.files?.[0]);
  }
  async function upload() {
    if (!file || busy) return;
    setBusy(true); setError(""); setResult(null);
    const body = new FormData();
    body.append("appointment_file", file);
    try {
      setResult(await send(path, body, "POST"));
      setFile(null);
      if (input.current) input.current.value = "";
      setTick((n) => n + 1);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  const rows = (uploads || []).map((u) => ({
    id: u.id,
    uploaded: formatDate(String(u.uploaded_at || "")),
    file: u.file_name || "Report",
    new_jobs: Number(u.jobs_new || 0),
    updated: Number(u.jobs_updated || 0),
    bookings_from: u.period_start ? periodText(u.period_start, u.period_end) : null,
    sent_by: u.by_you ? "Your team" : "Your agency",
  }));
  return (
    <div className="workflow-stack portal-report-upload">
      <section className="panel workflow-panel">
        {/* The page title already says "Appointment report"; this card is the upload itself. */}
        <h2>Upload a report</h2>
        <p className="workflow-note">Export this month's appointments so far and drop the file here. Jobs already sent are updated, never counted twice.</p>
        <label className={`report-drop${over ? " is-over" : ""}`} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={drop}>
          <UploadCloud aria-hidden="true" />
          <strong>{file ? file.name : "Drop the file here, or choose it"}</strong>
          <small>.xlsx or .csv, up to 4 MB</small>
          <input ref={input} type="file" accept=".xlsx,.xls,.csv" onChange={(e) => choose(e.target.files?.[0])} disabled={busy} />
        </label>
        <div className="report-actions">
          <button type="button" className="button" disabled={!file || busy} onClick={() => void upload()}>{busy ? "Uploading. This can take up to a minute…" : "Upload report"}</button>
        </div>
        {error && <p className="inline-notice danger" role="alert">{error}</p>}
        {result && (
          <div className="inline-notice" role="status">
            <p><strong>Got it, thank you.</strong> {result.file_name}</p>
            {result.jobs
              ? <p>{plural(result.jobs.new, "new job")}, {result.jobs.updated.toLocaleString("en-US")} updated{result.jobs.period ? `, booked ${periodText(result.jobs.period.start, result.jobs.period.end)}` : ""}.</p>
              : <p>The file arrived, but its jobs could not be read. Your agency will follow up.</p>}
            {result.google_ads && <p>{result.google_ads.status === "ok" ? (result.google_ads.sent ? `${plural(result.google_ads.sent, "booking")} sent to Google Ads.` : "Nothing new to send to Google Ads.") : "Saved. Google Ads did not take the bookings yet; NOLA tries again tonight."}</p>}
            {result.sheet_problems > 0 && <p>One of the Google Ads sheets could not be updated. Your agency will follow up.</p>}
          </div>
        )}
      </section>
      <section className="panel">
        <div className="panel-heading"><div><h2>Recent uploads</h2></div></div>
        {listError ? <ErrorState message={listError} onRetry={() => setTick((n) => n + 1)} /> : !uploads ? <LoadingState label="Loading uploads" /> :
          <DataTable rows={rows} columns={["uploaded", "file", "new_jobs", "updated", "bookings_from", "sent_by"]} empty="No reports uploaded yet." />}
      </section>
    </div>
  );
}
